import { HERO_IMAGES, LOGO_PRIMARY } from "@/lib/constants";
import { ArrowDown, MapPin, Sparkles } from "lucide-react";

export const Hero = ({ onStartBuilder }) => {
  return (
    <section
      data-testid="hero-section"
      className="relative border-b-2 border-black bg-white"
    >
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-5 py-14 md:grid-cols-12 md:gap-12 md:px-10 md:py-24">
        {/* LEFT */}
        <div className="md:col-span-7">
          <div className="mb-6 inline-flex items-center gap-2 border-2 border-black bg-[#9D4CDD] px-3 py-1.5">
            <Sparkles className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
            <span
              data-testid="hero-eyebrow"
              className="font-display text-[10px] font-bold uppercase tracking-[0.25em] text-white"
            >
              Ayrshire · Scotland · Summer 2026
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display text-5xl font-black leading-[0.95] tracking-tighter text-black sm:text-6xl md:text-[88px] lg:text-[104px]"
          >
            Are you
            <br />
            <span className="inline-block">
              <span className="text-[#9D4CDD]">summer</span> ready?
            </span>
          </h1>

          <p
            data-testid="hero-subhead"
            className="mt-7 max-w-xl font-display text-xl font-medium leading-snug text-black md:text-2xl"
          >
            Give your garden a makeover. Build your perfect outdoor space —
            mix and match real services, stay under{" "}
            <span className="bg-black px-1.5 text-white">£2,000</span> total.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <button
              type="button"
              data-testid="hero-cta-start"
              onClick={onStartBuilder}
              className="group inline-flex items-center gap-3 border-2 border-black bg-black px-7 py-4 font-display text-sm font-bold uppercase tracking-[0.2em] text-white transition-all hover:bg-[#9D4CDD] hover:border-[#9D4CDD]"
            >
              Start your makeover
              <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-1" />
            </button>
            <a
              href="#how-it-works"
              data-testid="hero-cta-how"
              className="inline-flex items-center gap-2 border-b-2 border-black pb-1 font-display text-sm font-bold uppercase tracking-[0.2em] text-black hover:text-[#9D4CDD] hover:border-[#9D4CDD]"
            >
              How it works
            </a>
          </div>

          {/* Stats strip */}
          <div className="mt-12 grid grid-cols-3 border-2 border-black">
            <div className="border-r-2 border-black p-4 md:p-5">
              <div className="font-display text-3xl font-black text-black md:text-4xl">
                £2k
              </div>
              <div className="mt-1 font-display text-[10px] font-bold uppercase tracking-[0.2em] text-black/70">
                Fixed budget
              </div>
            </div>
            <div className="border-r-2 border-black p-4 md:p-5">
              <div className="font-display text-3xl font-black text-[#9D4CDD] md:text-4xl">
                17
              </div>
              <div className="mt-1 font-display text-[10px] font-bold uppercase tracking-[0.2em] text-black/70">
                Services
              </div>
            </div>
            <div className="p-4 md:p-5">
              <div className="flex items-center gap-1.5 font-display text-3xl font-black text-black md:text-4xl">
                <MapPin className="h-6 w-6" />
                AYR
              </div>
              <div className="mt-1 font-display text-[10px] font-bold uppercase tracking-[0.2em] text-black/70">
                Local team
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT — Bento grid */}
        <div className="md:col-span-5">
          <div className="grid grid-cols-6 grid-rows-6 gap-3 h-[440px] md:h-[560px]">
            <div className="col-span-4 row-span-4 border-2 border-black overflow-hidden">
              <img
                src={HERO_IMAGES[0]}
                alt="Modern garden landscape"
                className="h-full w-full object-cover"
                data-testid="hero-img-1"
              />
            </div>
            <div className="col-span-2 row-span-2 border-2 border-black bg-[#9D4CDD] p-4 flex flex-col justify-between">
              <div className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-white">
                Trusted
              </div>
              <div className="font-display text-3xl font-black leading-none text-white md:text-4xl">
                ★★★★★
              </div>
            </div>
            <div className="col-span-2 row-span-2 border-2 border-black overflow-hidden">
              <img
                src={HERO_IMAGES[1]}
                alt="Lush green lawn"
                className="h-full w-full object-cover"
                data-testid="hero-img-2"
              />
            </div>
            <div className="col-span-2 row-span-2 border-2 border-black overflow-hidden bg-white flex items-center justify-center p-2">
              <img
                src={LOGO_PRIMARY}
                alt="Home Guard Developments badge"
                className="h-full w-full object-contain"
                data-testid="hero-logo-badge"
              />
            </div>
            <div className="col-span-2 row-span-2 border-2 border-black overflow-hidden">
              <img
                src={HERO_IMAGES[2]}
                alt="Outdoor seating area"
                className="h-full w-full object-cover"
                data-testid="hero-img-3"
              />
            </div>
            <div className="col-span-2 row-span-2 border-2 border-black overflow-hidden">
              <img
                src={HERO_IMAGES[3]}
                alt="Patio paving"
                className="h-full w-full object-cover"
                data-testid="hero-img-4"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Slogan marquee */}
      <div className="border-t-2 border-black bg-black overflow-hidden">
        <div className="hg-marquee flex whitespace-nowrap py-3">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex shrink-0 items-center">
              {[
                "ARE YOU SUMMER READY?",
                "GIVE YOUR GARDEN A MAKEOVER",
                "AYRSHIRE · SCOTLAND",
                "£2,000 BUDGET · ZERO SURPRISES",
              ]
                .concat([
                  "ARE YOU SUMMER READY?",
                  "GIVE YOUR GARDEN A MAKEOVER",
                  "AYRSHIRE · SCOTLAND",
                  "£2,000 BUDGET · ZERO SURPRISES",
                ])
                .map((t, i) => (
                  <span
                    key={`${k}-${i}`}
                    className="mx-6 font-display text-sm font-bold uppercase tracking-[0.3em] text-white md:text-base"
                  >
                    {t}
                    <span className="ml-6 text-[#9D4CDD]">✺</span>
                  </span>
                ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
